import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { AddressConfirmation, Lead } from '../api/types';
import { MutationError } from '../components/MutationError';
import { isAssumed, reviewStatuses } from '../geo/labels';
import { mapsSearchUrl } from '../places/maps';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// Adresse im Gespräch bestätigen (OFFENE_FRAGEN D-5): Leads, die das System nur nach Wahrscheinlichkeit zugeordnet hat
// (Vermerk ASSUMED_LOCATION), bestätigt die Telefonakquise nebenbei – oder trägt die Adresse ein, die der Kunde nennt.
// Unklare Adressen (Klärungsstatus) lassen sich hier ebenfalls richtigstellen.

type Details = { problems?: string[]; message?: string } | null;

export function AddressConfirm({ lead }: { lead: Lead }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    street: lead.street ?? '',
    house_number: lead.house_number ?? '',
    postal_code: lead.postal_code ?? '',
    city: lead.city ?? '',
  });
  const confirm = useMutation({
    mutationFn: (body: AddressConfirmation) => papi.confirmAddress(lead.id, body),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: projectKeys.focus(project.id, lead.id) });
    },
  });
  const details = lead.geo_details as Details;
  const assumed = isAssumed(lead);
  const unclear = (reviewStatuses as readonly string[]).includes(lead.geo_status);
  if (!can('lead.edit')) return null;
  if (!assumed && !unclear) {
    return confirm.data ? (
      <p role="status" className="alert alert-success">
        Adresse bestätigt.
      </p>
    ) : null;
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    confirm.mutate({
      street: form.street.trim() || null,
      house_number: form.house_number.trim() || null,
      postal_code: form.postal_code.trim() || null,
      city: form.city.trim() || null,
    });
  };
  return (
    <section className={`alert ${assumed ? 'alert-warn' : 'alert-info'} address-confirm`} aria-label="Adresse im Gespräch bestätigen">
      <p>
        <strong>{assumed ? 'Adresse bitte im Gespräch bestätigen' : 'Adresse unklar – bitte im Gespräch erfragen'}</strong>
      </p>
      {details?.message && <p className="address-confirm-reason">{details.message}</p>}
      <p>
        <a className="maps-link" href={mapsSearchUrl(lead.street, lead.house_number, lead.postal_code, lead.city)} target="_blank" rel="noopener noreferrer">
          Adresse in Google Maps ansehen
        </a>
      </p>
      {confirm.data && confirm.data.geo_source !== 'MANUAL' && (
        <p role="status">Die Adresse passt noch nicht eindeutig zusammen – bitte PLZ und Ort prüfen.</p>
      )}
      {editing ? (
        <form className="form-grid" onSubmit={submit}>
          <label className="field">
            <span>Straße</span>
            <input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
          </label>
          <label className="field narrow">
            <span>Hausnr.</span>
            <input value={form.house_number} onChange={(e) => setForm({ ...form, house_number: e.target.value })} />
          </label>
          <label className="field narrow">
            <span>PLZ</span>
            <input inputMode="numeric" maxLength={5} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
          </label>
          <label className="field">
            <span>Ort</span>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-small" disabled={confirm.isPending}>
              Adresse speichern
            </button>
            <button type="button" className="btn btn-small" onClick={() => setEditing(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <div className="form-actions">
          {assumed && (
            <button type="button" className="btn btn-primary btn-small" disabled={confirm.isPending} onClick={() => confirm.mutate({})}>
              Stimmt so
            </button>
          )}
          <button type="button" className="btn btn-small" onClick={() => setEditing(true)}>
            {assumed ? 'Korrigieren' : 'Adresse eintragen'}
          </button>
        </div>
      )}
      {confirm.isError && <MutationError error={confirm.error} />}
    </section>
  );
}
